import {
  ChangeDetectionStrategy,
  Component,
  type OnDestroy,
} from '@angular/core';
import { Storage, getDownloadURL, ref } from '@angular/fire/storage';
import { Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RecallsService } from '@principle-theorem/ng-principle';
import {
  CurrentBrandScope,
  CurrentScopeFacade,
} from '@principle-theorem/ng-principle-shared';
import { PUBLIC_REPORTS } from '@principle-theorem/ng-reporting';
import {
  ImageSize,
  TrackByFunctions,
  TypedFormControl,
  TypedFormGroup,
  getImageAtSize,
  isDisabled$,
  type IBreadcrumb,
} from '@principle-theorem/ng-shared';
import { Brand, Practice, Recalls } from '@principle-theorem/principle-core';
import {
  ADD_TO_WAITLIST_BY_DEFAULT,
  AppointmentConfirmationOptionalInfo,
  BridgeDeviceFeatureStatus,
  DEFAULT_SUCCESS_MESSAGE,
  IBrand,
  IBrandSettings,
  ITrackingCodes,
  OPTIONAL_APPOINTMENT_CONFIRMATION_INFO,
  OPTIONAL_PATIENT_FIELDS,
  RecallTrack,
  REPORTING_JOBS_DISPLAY,
  RESTRICT_TO_PRE_BLOCKS_BY_DEFAULT,
  type PatientOptionalField,
} from '@principle-theorem/principle-core/interfaces';
import {
  DEFAULT_TIMEZONE,
  Firestore,
  TIMEZONES,
  VALID_URL,
  filterUndefined,
  multiMap,
  reduce2DArray,
  shareReplayCold,
  slugify,
  snapshot,
  type Timezone,
  type WithRef,
} from '@principle-theorem/shared';
import { sortBy } from 'lodash';
import { Observable, Subject, combineLatest, from, of } from 'rxjs';
import {
  catchError,
  map,
  startWith,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators';
import { MARKETING_CAMPAIGNS_FEATURE } from '@principle-theorem/principle-core/features';

type IBrandFormData = {
  name: string;
  settings: Omit<IBrandSettings, 'recalls' | 'secondaryRecalls'>;
};

@Component({
  selector: 'pr-general-brand',
  templateUrl: './general-brand.component.html',
  styleUrls: ['./general-brand.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class GeneralBrandComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  trackByTimezone = TrackByFunctions.variable<Timezone>();
  brand$: Observable<WithRef<IBrand>>;
  hasIntegrations$: Observable<boolean>;
  timezones: Timezone[] = TIMEZONES;
  marketingFeature = MARKETING_CAMPAIGNS_FEATURE;

  trackingCodeForm = new TypedFormGroup<ITrackingCodes>({
    googleAnalytics4: new TypedFormControl<string[]>([]),
    googleTagManager: new TypedFormControl<string[]>([]),
    hubSpot: new TypedFormControl<string>(''),
    pixel: new TypedFormControl<string>(''),
    crossDomainUrls: new TypedFormControl<string[]>([]),
  });

  form = new TypedFormGroup<IBrandFormData>({
    name: new TypedFormControl('', Validators.required),
    settings: new TypedFormGroup<
      Omit<IBrandSettings, 'recalls' | 'secondaryRecalls' | 'paymentMethods'>
    >({
      timezone: new TypedFormControl(DEFAULT_TIMEZONE, Validators.required),
      scheduling: new TypedFormGroup<Required<IBrandSettings>['scheduling']>({
        defaultWaitlistSettingsOn: new TypedFormControl<boolean>(
          ADD_TO_WAITLIST_BY_DEFAULT
        ),
      }),
      onlineBookings: new TypedFormGroup<
        Required<
          Omit<IBrandSettings['onlineBookings'], 'treatmentTemplateOrder'>
        >
      >({
        restrictToPreBlocks: new TypedFormControl<boolean>(
          RESTRICT_TO_PRE_BLOCKS_BY_DEFAULT
        ),
        requestSuccessMessage: new TypedFormControl<string>(''),
        trackingCodes: this.trackingCodeForm,
        redirectUrl: new TypedFormControl<string>(
          '',
          Validators.pattern(VALID_URL)
        ),
      }),
      patientDetailsForm: new TypedFormGroup({
        requiredFields: new TypedFormControl<PatientOptionalField[]>([]),
      }),
      patient: new TypedFormGroup({
        requiredFields: new TypedFormControl<PatientOptionalField[]>([]),
      }),
      optionalDisplayInformation: new TypedFormGroup<
        Required<IBrandSettings>['optionalDisplayInformation']
      >({
        appointmentConfirmationInfo: new TypedFormControl<
          AppointmentConfirmationOptionalInfo[]
        >([]),
      }),
      treatmentPlanning: new TypedFormGroup({
        autoCompletePlanAfterLastStep: new TypedFormControl(false),
        includeCompletedTreatmentPlansInTimeline: new TypedFormControl(false),
      }),
      billing: new TypedFormGroup({
        enableBillingEntitySelection: new TypedFormControl(false),
      }),
      availableSystemReports: new TypedFormControl([]),
      displayMigratedChartFilter: new TypedFormControl<boolean>(false),
    }),
  });
  breadcrumbs$: Observable<IBreadcrumb[]>;
  patientFields = OPTIONAL_PATIENT_FIELDS;
  optionalAppointmentConfirmationInfo = OPTIONAL_APPOINTMENT_CONFIRMATION_INFO;
  isDisabled$ = isDisabled$(this.form);
  onlineBookingSuccessMessagePlaceholder = DEFAULT_SUCCESS_MESSAGE;
  managedReportOptions = sortBy(REPORTING_JOBS_DISPLAY, 'name');
  defaultReportsSummary = this._buildDefaultReportsSummary();
  logoUploadPath$: Observable<string>;
  brandLogoUrl$: Observable<string | undefined>;
  isUrlValidator = Validators.pattern(VALID_URL);
  readonly RecallTrack = RecallTrack;

  constructor(
    private _snackBar: MatSnackBar,
    private _storage: Storage,
    private _brandScope: CurrentBrandScope,
    currentScope: CurrentScopeFacade,
    public recalls: RecallsService
  ) {
    this.brand$ = this._brandScope.doc$.pipe(filterUndefined());
    this.brand$.pipe(take(1), takeUntil(this._onDestroy$)).subscribe((brand) =>
      this.form.patchValue(brand, {
        emitEvent: false,
      })
    );
    this.brandLogoUrl$ = this.brand$.pipe(
      switchMap((brand) => this._logoUrl$(brand, this._storage))
    );
    this.logoUploadPath$ = this.brand$.pipe(
      map((brand) => Brand.logoStoragePath(brand))
    );
    this.breadcrumbs$ = this.brand$.pipe(
      map((brand) => [
        { label: 'Settings', path: '../../../' },
        { label: brand.name },
        { label: 'General' },
      ])
    );
    this.hasIntegrations$ = currentScope.currentPractice$.pipe(
      filterUndefined(),
      switchMap((practice) => Practice.bridgeDevices$(practice)),
      multiMap((device) => device.features),
      reduce2DArray(),
      map((features) =>
        features.some(
          (feature) => feature.status === BridgeDeviceFeatureStatus.Enabled
        )
      )
    );
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  trackLabel(brand: WithRef<IBrand>, track: RecallTrack): string {
    return Recalls.trackLabel(brand, track);
  }

  integrationWarningMessage$(
    formGroupName: 'patient' | 'patientDetailsForm'
  ): Observable<string | undefined> {
    return combineLatest([
      this.form.valueChanges.pipe(
        startWith(this.form.value),
        map((changes) => changes.settings[formGroupName])
      ),
      this.hasIntegrations$,
    ]).pipe(
      map(([formValue, hasIntegrations]) => {
        const dateOfBirthRequired =
          formValue?.requiredFields?.includes('dateOfBirth');
        if (!dateOfBirthRequired && hasIntegrations) {
          return 'Patient date of birth is required for one or more active integrations';
        }
      })
    );
  }

  async save(): Promise<void> {
    const brand = await snapshot(this.brand$);
    const data = this.form.getRawValue();
    await Firestore.patchDoc(brand.ref, {
      name: data.name,
      slug: slugify(data.name.toLowerCase()),
      settings: {
        ...brand.settings,
        ...data.settings,
        onlineBookings: {
          ...brand.settings.onlineBookings,
          ...data.settings.onlineBookings,
        },
      },
    });
    this._snackBar.open('Brand Updated');
    this.form.markAsPristine();
    this.form.updateValueAndValidity();
  }

  async updateImageUrl(storagePath: string): Promise<void> {
    const brand = await snapshot(this.brand$);
    await Firestore.patchDoc(brand.ref, { logoUrl: storagePath });
  }

  async removeLogo(): Promise<void> {
    const brand = await snapshot(this.brand$);
    await Firestore.patchDoc(brand.ref, { logoUrl: '' });
  }

  private _buildDefaultReportsSummary(): string {
    const defaultReports = this.managedReportOptions
      .filter((report) => PUBLIC_REPORTS.includes(report.type))
      .map((report) => report.name)
      .join(', ');

    return `The following reports are available to all brands regardless of selection: ${defaultReports}`;
  }

  private _logoUrl$(
    brand: WithRef<IBrand>,
    storage: Storage,
    size: ImageSize = ImageSize.Medium
  ): Observable<string | undefined> {
    if (!brand.logoUrl) {
      return of(undefined);
    }

    const logoRef = ref(storage, getImageAtSize(brand.logoUrl, size, 'webp'));
    const fallbackRef = ref(storage, brand.logoUrl);

    return from(getDownloadURL(logoRef)).pipe(
      catchError(() => getDownloadURL(fallbackRef)),
      shareReplayCold()
    );
  }
}
